// Apolo IDE — PlanChartsView (sprint plan-charts).
//
// Dashboard do plano: cards de métrica no topo + 4 presets auto (do
// computePhaseEstimates / contagem de blocos) + os blocos kind=chart que o
// usuário criou no canvas. Render via ChartRenderer (lazy).

import { useMemo } from 'react';
import { Clock, Layers, Boxes } from 'lucide-react';
import ChartRenderer from './ChartRenderer.jsx';
import { computePhaseEstimates } from '../../lib/plan/estimates.js';
import { buildPresetSeries, chartMetrics, PRESETS } from '../../lib/plan/chartData.js';

function MetricCard({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-apolo-borderSubtle bg-apolo-surface1 px-4 py-3">
      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-apolo-plan-accentMuted text-apolo-plan-accent" aria-hidden="true">
        <Icon size={16} />
      </span>
      <div className="flex flex-col leading-tight">
        <span className="text-lg font-semibold tracking-tight text-apolo-textPrimary">{value}</span>
        <span className="text-[11px] text-apolo-textTertiary">{label}</span>
      </div>
    </div>
  );
}

function ChartCard({ title, type, series }) {
  return (
    <div className="flex flex-col rounded-xl border border-apolo-borderSubtle bg-apolo-surface1 p-3">
      <span className="mb-2 truncate text-xs font-semibold text-apolo-textPrimary">{title}</span>
      <div className="h-[220px]">
        <ChartRenderer type={type} series={series} height={220} />
      </div>
    </div>
  );
}

export default function PlanChartsView({ plan, blocks }) {
  const estimates = useMemo(() => computePhaseEstimates(blocks), [blocks]);
  const metrics = useMemo(() => chartMetrics(blocks, estimates), [blocks, estimates]);
  const presets = useMemo(() => PRESETS.map((p) => buildPresetSeries(p.id, blocks, estimates)), [blocks, estimates]);
  const chartBlocks = useMemo(() => (Array.isArray(blocks) ? blocks.filter((b) => b.kind === 'chart' && (b.payload?.series?.[0]?.data?.length)) : []), [blocks]);

  const hasPhaseData = estimates.phases.length > 0;

  return (
    <div className="flex-1 overflow-y-auto bg-apolo-bg p-4 sm:p-6">
      {/* Métricas */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <MetricCard icon={Clock} label="Horas estimadas" value={`${metrics.totalHours}h`} />
        <MetricCard icon={Layers} label="Fases ativas" value={metrics.phaseCount} />
        <MetricCard icon={Boxes} label="Blocos no plano" value={metrics.blockCount} />
      </div>

      {/* Presets do plano */}
      <h3 className="mb-2 mt-6 text-[11px] font-semibold uppercase tracking-wide text-apolo-textTertiary">Visão do sprint</h3>
      {hasPhaseData ? (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {presets.map((c) => (
            <ChartCard key={c.source} title={c.title} type={c.type} series={c.series} />
          ))}
        </div>
      ) : (
        <p className="rounded-lg border border-dashed border-apolo-borderSubtle p-4 text-center text-xs text-apolo-textTertiary">
          Adicione skills/headings de fase no canvas (ou use a barra Próximos Passos) pra ver horas, ROI e KPIs por fase aqui.
        </p>
      )}

      {/* Charts manuais do usuário */}
      {chartBlocks.length > 0 ? (
        <>
          <h3 className="mb-2 mt-6 text-[11px] font-semibold uppercase tracking-wide text-apolo-textTertiary">Seus gráficos</h3>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {chartBlocks.map((b) => (
              <ChartCard key={b.id} title={b.payload.title || 'Gráfico'} type={b.payload.type} series={b.payload.series} />
            ))}
          </div>
        </>
      ) : null}

      <p className="mt-6 text-center text-[10px] text-apolo-textTertiary">
        {plan?.name} · dashboard de charts · os dados vão pro .md ao exportar
      </p>
    </div>
  );
}
